import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ToastsService } from '@catch/services';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faPaperclip,
  faTimes,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FileDndDirective } from './file-dnd.directive';
import { FileInputChangeDirective } from './file-input-change.directive';
import { MIME_TYPE_LABELS } from './mime-type-labels';

/**
 * Input component that is attached to a {@link FormControl} to pick and select files via file explorer or drag and drop
 */
@Component({
  selector: 'ct-lib-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
  imports: [
    FileDndDirective,
    FileInputChangeDirective,
    FaIconComponent,
    TranslocoPipe,
  ],
})
export class FileInputComponent {
  private toastsService = inject(ToastsService);
  private translateService = inject(TranslocoService);

  private readonly FILE_SIZE_LIMIT: number = 20 * 1024 * 1024;
  private readonly TOTAL_FILE_SIZE_LIMIT: number = 64 * 1024 * 1024;
  @Input() control: UntypedFormControl = new UntypedFormControl(null, []);
  @Input() submitAttempt = false;
  @Input() label = '';
  @Input() multiple = true;
  @Input() accept = '*';
  @Input() showAcceptLabel = true;
  @Input() accentColor: string = '#6E8BEF';
  @Input() showIllustration = false;
  @Output() filesChange = new EventEmitter<void>();
  files: File[] = [];
  faTimes = faTimes;
  faPaperclip = faPaperclip;
  faTrash = faTrash;

  reset(): void {
    this.files = [];
    this.filesChange.emit();
  }

  appendFiles(files: FileList): void {
    if (!this.multiple && files.length > 0) {
      this.files = [];
    }

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (!file || this.files.includes(file)) continue;
      if (
        this.checkTypeAllowed(file) &&
        !this.fileSizeExceeded(file) &&
        !this.fileNameLengthExceeded(file) &&
        !this.totalFileSizeExceeded()
      ) {
        this.files.push(file);
        if (this.multiple) {
          this.control.patchValue(this.files);
        } else {
          this.control.patchValue(this.files[0]);
        }
      }
    }
    this.totalFileSizeExceeded();

    // Emit the files to parent component
    this.filesChange.emit();
  }

  removeFile(file: File) {
    this.files.splice(this.files.indexOf(file), 1);
    this.control.patchValue(this.files);
    this.filesChange.emit();
  }

  checkTypeAllowed(file: File) {
    const acceptedTypes: Array<string> = this.accept.split(/, ?/);
    if (acceptedTypes.includes('image/jpeg')) acceptedTypes.push('image/jpg');
    else if (acceptedTypes.includes('image/jpg'))
      acceptedTypes.push('image/jpeg');
    const allowed: boolean =
      this.accept === '*' ? true : acceptedTypes.includes(file.type);
    if (!allowed) {
      this.translateService
        .selectTranslate('ct-library.image-editor.wrong-type-toast', {
          fileName: file.name,
        })
        .subscribe({
          next: (result: string) => {
            this.toastsService.alert(result);
          },
        });
    }
    return allowed;
  }

  /**
   * Accepted formats for the hint, e.g. `application/pdf` → `PDF`.
   * Uses {@link MIME_TYPE_LABELS}; unknown types fall back to subtype / extension.
   * @returns Comma separated format names
   */
  get acceptLabel(): string {
    const names = this.accept
      .split(/, ?/)
      .map((type) => this.toAcceptFormatLabel(type))
      .filter((label): label is string => !!label);
    return [...new Set(names)].join(', ');
  }

  /**
   * Maps one accept token to a short display label.
   * @param type MIME type or extension from the `accept` input
   * @returns Label, or `null` when the type should be omitted
   */
  private toAcceptFormatLabel(type: string): string | null {
    const trimmed = type.trim();
    if (!trimmed || trimmed === '*') return null;

    if (MIME_TYPE_LABELS.has(trimmed)) {
      return MIME_TYPE_LABELS.get(trimmed) ?? null;
    }

    return (trimmed.split('/').pop() ?? trimmed).replace(/^\./, '').toUpperCase();
  }

  get totalFileSize() {
    let result = 0;
    this.files.forEach((file: File) => (result += file.size));
    return result;
  }

  getFileSizeLimitInMb() {
    return Math.round(this.FILE_SIZE_LIMIT / 1024 / 1024);
  }

  fileSizeExceeded(file: File): boolean {
    const exceeded: boolean = file.size > this.FILE_SIZE_LIMIT;
    if (exceeded) {
      this.translateService
        .selectTranslate('ct-library.file-input.file-size-exceeded', {
          fileName: file.name,
        })
        .subscribe({
          next: (result: string) => {
            this.toastsService.warning(result);
          },
        });
    }
    return exceeded;
  }

  totalFileSizeExceeded(): boolean {
    const exceeded: boolean = this.totalFileSize > this.TOTAL_FILE_SIZE_LIMIT;
    if (exceeded) {
      this.translateService
        .selectTranslate('ct-library.file-input.total-file-size-exceeded')
        .subscribe({
          next: (result: string) => {
            this.toastsService.warning(result);
          },
        });
    }
    return exceeded;
  }

  fileNameLengthExceeded(file: File): boolean {
    const exceeded = file.name.length > 250;
    if (exceeded) {
      this.translateService
        .selectTranslate('ct-library.file-input.file-name-length-exceeded')
        .subscribe({
          next: (result: string) => {
            this.toastsService.warning(result);
          },
        });
    }
    return exceeded;
  }
}
